import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Building2, Cloud, Truck, Mail, Users, Warehouse, CheckCircle2, XCircle,
  ExternalLink, ChevronRight, Upload, Trash2, RefreshCw, Save, Package,
  Database, Download, HardDrive, RotateCcw, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function BackupSection() {
  const [backups, setBackups] = useState([]);
  const [schedule, setSchedule] = useState({ enabled: false, frequency: "daily", time: "03:00", upload_to_nextcloud: false });
  const [nc, setNc] = useState({ url: "", username: "", password: "", folder: "/POPCRN-Backups", password_set: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [b, s, n] = await Promise.all([
        api.get("/backup/list"),
        api.get("/backup/schedule"),
        api.get("/backup/nextcloud"),
      ]);
      setBackups(b.data); setSchedule(s.data); setNc({ ...n.data, password: "" });
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const createNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backup/create"); toast.success(`Backup erstellt · ${data.filename}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  const download = (fn) => {
    const t = localStorage.getItem("popcrn_token");
    fetch(`${API_BASE}/backup/download/${fn}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = fn; a.click();
        URL.revokeObjectURL(url);
      });
  };
  const del = async (fn) => {
    if (!window.confirm(`Backup "${fn}" löschen?`)) return;
    try { await api.delete(`/backup/${fn}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const restore = async (fn) => {
    if (!window.confirm(`Backup "${fn}" wiederherstellen?\n\nDIES ÜBERSCHREIBT ALLE AKTUELLEN DATEN.\nVor der Wiederherstellung wird automatisch ein Sicherheits-Backup erstellt.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/backup/restore/${fn}`); toast.success(`Wiederhergestellt: ${Object.keys(data.restored).length} Collections`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  const saveSchedule = async () => {
    try { const { data } = await api.post("/backup/schedule", schedule); setSchedule(data); toast.success("Zeitplan gespeichert"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const saveNc = async () => {
    try { const { data } = await api.post("/backup/nextcloud", nc); setNc({ ...data, password: "" }); toast.success("Nextcloud gespeichert"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const testNc = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backup/nextcloud/test"); toast.success(data.message || "Verbunden"); }
    catch (e) { toast.error(e.response?.data?.detail || "Verbindung fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="backup-section">
      <div>
        <Button onClick={createNow} disabled={busy} data-testid="btn-backup-create"
                className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
          <Database size={14} className="mr-1" /> Backup jetzt erstellen
        </Button>
      </div>
      <div>
        <div className="text-sm font-semibold mb-2">Gespeicherte Backups (max. 10)</div>
        <div className="rounded-xl border border-[var(--border)] bg-white divide-y">
          {backups.length === 0 && <div className="p-3 text-xs text-[var(--text-muted)]">Noch keine Backups.</div>}
          {backups.map((b) => (
            <div key={b.filename} className="flex items-center justify-between p-2.5 text-xs" data-testid={`backup-row-${b.filename}`}>
              <div className="min-w-0">
                <div className="font-mono truncate">{b.filename}</div>
                <div className="text-[10px] text-[var(--text-muted)]">
                  {new Date(b.created_at).toLocaleString("de-DE")} · {(b.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => download(b.filename)} className="rounded-full h-7 px-2"><Download size={12} /></Button>
                <Button size="sm" variant="outline" onClick={() => restore(b.filename)} disabled={busy} className="rounded-full h-7 px-2" data-testid={`btn-restore-${b.filename}`}><RotateCcw size={12} /></Button>
                <Button size="sm" variant="outline" onClick={() => del(b.filename)} className="rounded-full h-7 px-2 text-red-600 border-red-300"><Trash2 size={12} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t pt-4">
        <div className="text-sm font-semibold mb-2">Automatisches Backup</div>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Aktiv</Label>
            <Switch checked={schedule.enabled}
                    onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })}
                    data-testid="switch-backup-schedule" />
          </div>
          <div>
            <Label>Häufigkeit</Label>
            <Select value={schedule.frequency} onValueChange={(v) => setSchedule({ ...schedule, frequency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Täglich</SelectItem>
                <SelectItem value="weekly">Wöchentlich</SelectItem>
                <SelectItem value="monthly">Monatlich</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Uhrzeit (UTC)</Label>
            <Input type="time" value={schedule.time} onChange={(e) => setSchedule({ ...schedule, time: e.target.value })} /></div>
          <div className="flex items-center gap-2">
            <Switch checked={schedule.upload_to_nextcloud}
                    onCheckedChange={(v) => setSchedule({ ...schedule, upload_to_nextcloud: v })}
                    data-testid="switch-backup-nc" />
            <Label className="text-xs">In Nextcloud hochladen</Label>
          </div>
        </div>
        <Button size="sm" onClick={saveSchedule} data-testid="btn-save-backup-schedule"
                className="mt-3 rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
          <Save size={12} className="mr-1" /> Zeitplan speichern
        </Button>
        {schedule.last_run && <div className="text-[10px] text-[var(--text-muted)] mt-2">Zuletzt: {new Date(schedule.last_run).toLocaleString("de-DE")}</div>}
      </div>
      <div className="border-t pt-4">
        <div className="text-sm font-semibold mb-2">Nextcloud-Anbindung</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Server-URL</Label>
            <Input value={nc.url} onChange={(e) => setNc({ ...nc, url: e.target.value })} placeholder="https://cloud.example.de" data-testid="input-nc-url" /></div>
          <div><Label>Benutzername</Label>
            <Input value={nc.username} onChange={(e) => setNc({ ...nc, username: e.target.value })} data-testid="input-nc-user" /></div>
          <div><Label>App-Passwort {nc.password_set && <span className="text-[10px] text-green-600">(gespeichert)</span>}</Label>
            <Input type="password" value={nc.password} onChange={(e) => setNc({ ...nc, password: e.target.value })} placeholder={nc.password_set ? "•••••••• (leer lassen = unverändert)" : ""} data-testid="input-nc-pw" /></div>
          <div><Label>Zielordner</Label>
            <Input value={nc.folder} onChange={(e) => setNc({ ...nc, folder: e.target.value })} data-testid="input-nc-folder" /></div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={saveNc} data-testid="btn-save-nc"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            <Save size={12} className="mr-1" /> Speichern
          </Button>
          <Button size="sm" variant="outline" onClick={testNc} disabled={busy} data-testid="btn-test-nc" className="rounded-full">
            <CheckCircle2 size={12} className="mr-1" /> Verbindung testen
          </Button>
        </div>
      </div>
    </div>
  );
}

function ShelvesImportCard() {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [purge, setPurge] = useState(true);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useState(null)[0] || { current: null };

  const doImport = async () => {
    if (!file) return toast.error("Bitte zuerst eine Datei wählen");
    if (purge && !window.confirm(`ALLE bestehenden Regale löschen und aus "${file.name}" neu anlegen?\n\nNicht rückgängig zu machen!`)) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/shelves/import-xlsx?purge_existing=${purge}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`✓ ${data.created} neu, ${data.updated} aktualisiert, ${data.purged} gelöscht`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="shelves-import-card"
         className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-4">
      <div className="font-display text-lg font-bold text-amber-900 mb-1">
        <Package className="inline mr-2 -mt-1" size={18} /> Regale aus Excel importieren
      </div>
      <div className="text-xs text-amber-900 mb-3">
        Lade eine <b>.xlsx-Datei</b> mit den Spalten <span className="font-mono">Regal, Ebene, Platz</span>{" "}
        (optional <span className="font-mono">Lagerplatzcode</span>, <span className="font-mono">Typ</span>,{" "}
        <span className="font-mono">Kapazität</span>). Mehrere Sheets werden zusammengeführt. Grid-Positionen werden aus dem Code (z. B. <span className="font-mono">A01-04-01</span>) automatisch berechnet.
        {" "}
        <a href={`${API_BASE}/shelves/import-xlsx/template`}
           target="_blank" rel="noreferrer"
           className="text-amber-800 font-bold underline hover:text-amber-900 inline-flex items-center gap-1"
           data-testid="link-shelves-template">
          📄 Excel-Vorlage herunterladen
        </a>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) setFile(f);
        }}
        className={`rounded-xl border-2 border-dashed p-4 text-center mb-2 transition-colors ${
          dragOver ? "border-amber-500 bg-amber-100" : "border-amber-300 bg-white"
        }`}>
        <input type="file" accept=".xlsx,.xlsm" className="hidden"
               data-testid="input-shelves-xlsx"
               ref={(el) => { inputRef.current = el; }}
               onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {file ? (
          <div className="text-sm">
            <div className="font-semibold text-amber-900" data-testid="shelves-import-filename">📄 {file.name}</div>
            <div className="text-xs text-amber-700">{(file.size / 1024).toFixed(1)} KB</div>
            <button onClick={() => setFile(null)}
                    className="text-xs text-red-600 hover:underline mt-1"
                    data-testid="btn-shelves-import-clear">Datei entfernen</button>
          </div>
        ) : (
          <>
            <Upload size={28} className="mx-auto text-amber-600 mb-1" />
            <div className="text-sm font-semibold mb-1">Datei hierhin ziehen oder wählen</div>
            <Button variant="outline" size="sm"
                    className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-100"
                    data-testid="btn-shelves-choose-file"
                    onClick={() => inputRef.current?.click()}>
              <Upload size={12} className="mr-1" /> Excel-Datei wählen
            </Button>
          </>
        )}
      </div>

      <label className="inline-flex items-center gap-2 text-xs mb-3 cursor-pointer">
        <input type="checkbox" checked={purge}
               data-testid="chk-shelves-import-purge"
               onChange={(e) => setPurge(e.target.checked)} />
        <span><b>Alle vorhandenen Regale zuerst löschen</b> (empfohlen bei kompletter Neuaufteilung)</span>
      </label>

      {result && (
        <div className="text-xs bg-white rounded-lg p-2 font-mono mb-2" data-testid="shelves-import-result">
          ✓ {result.created} neu · {result.updated} aktualisiert · {result.purged} gelöscht{" "}
          {result.errors?.length > 0 && (
            <span className="text-red-600">· {result.errors.length} Fehler</span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button variant="outline"
                data-testid="btn-shelves-import"
                onClick={doImport} disabled={!file || busy}
                className="rounded-full border-amber-400 text-amber-900 hover:bg-amber-100 font-bold">
          {busy ? "Importiere…" : (purge ? "Löschen & Importieren" : "Importieren")}
        </Button>
      </div>
    </div>
  );
}

function WarehouseLayoutCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const setup = async () => {
    if (!window.confirm("Standard-Lagerplan erzeugen? 6 Regale × 2 Seiten × 4 Ebenen = 120 Regalplätze (3 Paletten pro Ebene + 320 Einzelplätze pro Regalseite).")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/setup/warehouse-layout");
      setResult(r.data);
      toast.success(`${r.data.created} neu, ${r.data.skipped} bereits vorhanden`);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="warehouse-layout-card"
         className="rounded-xl border-2 border-blue-200 bg-blue-50/60 p-4">
      <div className="font-display text-lg font-bold text-blue-900 mb-1">📦 Standard-Lagerplan erzeugen</div>
      <div className="text-xs text-blue-800 mb-3">
        Erstellt <b>6 Regale in einer Reihe</b>, je Regal Vorder- und Rückseite (freistehend).
        Pro Rack-Seite 4 Ebenen: 3 Palettenstellplätze oben & mittig & unten + 320 Einzelproduktstellplätze in der Kommissionierebene.
        Insgesamt <b>120 Regalplätze</b>. Idempotent — vorhandene Codes werden übersprungen.
      </div>
      {result && (
        <div className="text-xs bg-white rounded-lg p-2 font-mono mb-2">
          ✓ {result.created} neu · {result.skipped} übersprungen · {result.total_shelves} gesamt
        </div>
      )}
      <Button variant="outline" data-testid="btn-setup-warehouse"
              onClick={setup} disabled={busy}
              className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-100">
        {busy ? "Erzeuge…" : "Lagerplan erzeugen"}
      </Button>
    </div>
  );
}

function CleanupOrdersCard() {
  const [step, setStep] = useState(0);    // 0 idle, 1 first confirm, 2 second confirm
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const doCleanup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup/orders");
      setResult(r.data.deleted);
      toast.success(`${r.data.deleted.sales_orders} Bestellungen & ${r.data.deleted.delivery_notes} Lieferscheine gelöscht`);
    } catch (err) { toast.error(err.response?.data?.detail || "Cleanup fehlgeschlagen"); }
    finally { setBusy(false); setStep(0); }
  };
  return (
    <div data-testid="cleanup-orders-card"
         className="rounded-xl border-2 border-red-200 bg-red-50/60 p-4">
      <div className="font-display text-lg font-bold text-red-900 mb-1">⚠️ Testbestellungen löschen</div>
      <div className="text-xs text-red-800 mb-3">
        Entfernt <b>alle</b> Kundenbestellungen, Lieferscheine und die zugehörigen Lagerbewegungen.
        Stammdaten (Artikel, Kunden, Chargen, Rezepturen) bleiben unangetastet. <b>Nicht rückgängig zu machen!</b>
      </div>
      {result ? (
        <div className="text-xs bg-white rounded-lg p-2 font-mono">
          ✓ {result.sales_orders} Bestellungen · {result.delivery_notes} Lieferscheine · {result.stock_movements} Movements gelöscht
        </div>
      ) : step === 0 ? (
        <Button variant="outline" data-testid="btn-cleanup-orders-init"
                onClick={() => setStep(1)}
                className="rounded-full border-red-300 text-red-700 hover:bg-red-100">
          Testbestellungen löschen
        </Button>
      ) : step === 1 ? (
        <div className="flex items-center gap-2">
          <div className="text-sm text-red-900 font-semibold flex-1">Wirklich alle Bestellungen löschen?</div>
          <Button variant="outline" onClick={() => setStep(0)} className="rounded-full">Abbrechen</Button>
          <Button data-testid="btn-cleanup-orders-confirm1"
                  onClick={() => setStep(2)}
                  className="rounded-full bg-red-600 text-white hover:bg-red-700 font-bold">
            Ja, weiter
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="text-sm text-red-900 font-semibold flex-1">Letzte Warnung — endgültig löschen?</div>
          <Button variant="outline" onClick={() => setStep(0)} className="rounded-full">Abbrechen</Button>
          <Button data-testid="btn-cleanup-orders-confirm2"
                  onClick={doCleanup} disabled={busy}
                  className="rounded-full bg-red-700 text-white hover:bg-red-800 font-bold">
            {busy ? "Lösche…" : "ENDGÜLTIG löschen"}
          </Button>
        </div>
      )}
    </div>
  );
}

function StatusPill({ ok, label }) {
  return ok
    ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#d1fae5] text-[#065f46]"><CheckCircle2 size={12}/> {label || "Verbunden"}</span>
    : <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#fee2e2] text-[#991b1b]"><XCircle size={12}/> {label || "Nicht konfiguriert"}</span>;
}

function Section({ icon: Icon, title, subtitle, children, right }) {
  return (
    <Card className="p-6 border border-[var(--border)] rounded-2xl mb-4">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--pop-pink)]/10 flex items-center justify-center text-[var(--pop-pink)]">
            <Icon size={20} />
          </div>
          <div>
            <div className="font-display text-xl font-bold">{title}</div>
            {subtitle && <div className="text-xs text-[var(--text-muted)]">{subtitle}</div>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </Card>
  );
}

function ProductGroupsSection() {
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [color, setColor] = useState("#eb7208");

  const load = async () => {
    try { setGroups((await api.get("/product-groups")).data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Name erforderlich");
    try {
      await api.post("/product-groups", { name: name.trim(), code: code.trim() || null, color });
      toast.success("Warengruppe angelegt");
      setName(""); setCode("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const saveField = async (g, patch) => {
    try {
      await api.patch(`/product-groups/${g.id}`, patch);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Warengruppe „${g.name}" löschen?`)) return;
    try { await api.delete(`/product-groups/${g.id}`); toast.success("Gelöscht"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <Section icon={Package} title="Warengruppen"
             subtitle="Kürzel wird als 3-stellige Warengruppen-Nummer in Auto-Chargennummern verwendet (z.B. 2026-09-01-601).">
      <div data-testid="section-product-groups" className="space-y-3">
        <div className="grid grid-cols-[1fr_120px_100px_auto] gap-2 items-end">
          <div><Label>Name</Label><Input value={name} data-testid="input-pg-name"
                onChange={(e) => setName(e.target.value)} placeholder="z.B. Karamell" /></div>
          <div><Label>Kürzel</Label><Input value={code} data-testid="input-pg-code"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                placeholder="z.B. 601" maxLength={3} /></div>
          <div><Label>Farbe</Label><Input type="color" value={color}
                onChange={(e) => setColor(e.target.value)} /></div>
          <Button onClick={create} data-testid="btn-add-pg"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            Hinzufügen
          </Button>
        </div>
        <div className="border border-[var(--border)] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--pop-cream)]/30">
              <tr>
                <th className="text-left p-2 overline text-xs">Name</th>
                <th className="text-left p-2 overline text-xs">Kürzel (3-stellig)</th>
                <th className="text-left p-2 overline text-xs">Farbe</th>
                <th className="text-right p-2 overline text-xs">Aktion</th>
              </tr>
            </thead>
            <tbody data-testid="tbody-product-groups">
              {groups.map((g) => (
                <tr key={g.id} className="border-t border-[var(--border)]">
                  <td className="p-2 font-medium">{g.name}</td>
                  <td className="p-2">
                    <Input defaultValue={g.code || ""} data-testid={`input-pg-code-${g.id}`}
                           onBlur={(e) => {
                             const v = e.target.value.replace(/\D/g, "").slice(0, 3);
                             if (v !== (g.code || "")) saveField(g, { code: v || null });
                           }} className="h-8 w-24 font-mono" placeholder="—" maxLength={3} />
                  </td>
                  <td className="p-2"><span className="inline-block w-6 h-6 rounded"
                    style={{ background: g.color || "#eb7208" }} /></td>
                  <td className="p-2 text-right">
                    <button onClick={() => remove(g)}
                            data-testid={`btn-delete-pg-${g.id}`}
                            className="text-red-600 hover:underline text-xs font-semibold">
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
              {groups.length === 0 && (
                <tr><td colSpan={4} className="p-4 text-center text-xs text-[var(--text-muted)]">
                  Noch keine Warengruppen angelegt.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}

function WarehouseFefoCard() {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/warehouse").then((r) => setDays(r.data.fefo_mhd_tolerance_days ?? 30)).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/warehouse", { fefo_mhd_tolerance_days: Number(days) });
      toast.success("MHD-Toleranz gespeichert");
    } catch (e) { toast.error("Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={Package} title="FEFO MHD-Toleranz"
             subtitle="Bei voller Chargen-Abdeckung (z. B. ganze Palette) wird ein Split vermieden, solange die MHD-Differenz unter diesem Schwellenwert liegt.">
      <div className="flex items-end gap-3" data-testid="warehouse-fefo-card">
        <div>
          <Label className="text-xs">Toleranz in Tagen</Label>
          <Input type="number" min="0" value={days} data-testid="fefo-tolerance-input"
                 onChange={(e) => setDays(e.target.value)} className="w-32" />
        </div>
        <Button onClick={save} disabled={busy} data-testid="fefo-tolerance-save"
                className="rounded-full bg-[var(--pop-pink)] text-white font-bold">
          {busy ? "Speichern…" : "Speichern"}
        </Button>
      </div>
    </Section>
  );
}

function Display120DefaultCard() {
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/items", { params: { type: "finished", page_size: 1000 } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
    api.get("/settings/display-120").then((r) => {
      const rws = r.data.rows || [];
      setRows([0, 1, 2, 3, 4, 5].map((i) => rws[i] || ""));
    }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/display-120", { rows: rows.filter(Boolean) });
      toast.success("120er-Standard-Mix gespeichert");
    } catch (e) { toast.error("Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={Package} title="120er-Display Standard-Mix"
             subtitle="6 Reihen × 20 Stück — Vorbelegung beim Anlegen einer 120er-Bestellung">
      <div className="space-y-2" data-testid="display120-default-card">
        {rows.map((rid, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs w-16 shrink-0 text-[var(--text-muted)]">Reihe {i + 1}</span>
            <select value={rid} onChange={(e) => { const c = [...rows]; c[i] = e.target.value; setRows(c); }}
                    data-testid={`display120-row-${i}`}
                    className="flex-1 rounded-lg border border-[var(--border)] py-2 px-2 text-sm bg-white">
              <option value="">— Artikel wählen —</option>
              {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
            </select>
          </div>
        ))}
        <Button onClick={save} disabled={busy} data-testid="display120-save"
                className="rounded-full bg-[var(--pop-pink)] text-white font-bold mt-2">
          {busy ? "Speichern…" : "Standard-Mix speichern"}
        </Button>
      </div>
    </Section>
  );
}

function ShippingClassesCard() {
  const [cfg, setCfg] = useState({ free_threshold_net: 100, vat_rate: 0.19, classes: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/shipping").then((r) => setCfg({
      free_threshold_net: r.data.free_threshold_net ?? 100,
      vat_rate: r.data.vat_rate ?? 0.19,
      classes: r.data.classes || [],
    })).catch(() => {});
  }, []);
  const setClass = (i, k, v) => setCfg((c) => { const cl = [...c.classes]; cl[i] = { ...cl[i], [k]: v }; return { ...c, classes: cl }; });
  const addClass = () => setCfg((c) => ({ ...c, classes: [...c.classes, { max_weight_g: "", price_net: "", label: "" }] }));
  const delClass = (i) => setCfg((c) => ({ ...c, classes: c.classes.filter((_, j) => j !== i) }));
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/settings/shipping", {
        free_threshold_net: Number(cfg.free_threshold_net),
        vat_rate: Number(cfg.vat_rate),
        classes: cfg.classes.map((c) => ({ max_weight_g: Number(c.max_weight_g), price_net: Number(c.price_net), label: c.label || "" })),
      });
      toast.success("Versandklassen gespeichert");
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <Section icon={Truck} title="Versandkosten & DHL-Gewichtsklassen"
             subtitle="Portal: Unter dem Mindestbestellwert werden Versandkosten anhand des Gesamtgewichts berechnet (netto zzgl. MwSt.).">
      <div className="space-y-4" data-testid="shipping-classes-card">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs">Versandkostenfrei ab (€ netto)</Label>
            <Input type="number" min="0" step="0.01" value={cfg.free_threshold_net} data-testid="shipping-free-threshold"
                   onChange={(e) => setCfg((c) => ({ ...c, free_threshold_net: e.target.value }))} className="w-40" />
          </div>
          <div>
            <Label className="text-xs">MwSt.-Satz Versand (z. B. 0.19)</Label>
            <Input type="number" min="0" step="0.01" value={cfg.vat_rate} data-testid="shipping-vat-rate"
                   onChange={(e) => setCfg((c) => ({ ...c, vat_rate: e.target.value }))} className="w-40" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_120px_120px_40px] gap-2 text-[11px] font-semibold text-[var(--text-muted)] px-1">
            <span>Bezeichnung</span><span>bis Gewicht (g)</span><span>Preis netto (€)</span><span></span>
          </div>
          {cfg.classes.map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_120px_40px] gap-2 items-center" data-testid={`shipping-class-${i}`}>
              <Input value={c.label || ""} placeholder="DHL Paket bis 2 kg" data-testid={`shipping-class-label-${i}`}
                     onChange={(e) => setClass(i, "label", e.target.value)} />
              <Input type="number" min="0" value={c.max_weight_g} data-testid={`shipping-class-weight-${i}`}
                     onChange={(e) => setClass(i, "max_weight_g", e.target.value)} />
              <Input type="number" min="0" step="0.01" value={c.price_net} data-testid={`shipping-class-price-${i}`}
                     onChange={(e) => setClass(i, "price_net", e.target.value)} />
              <Button variant="ghost" size="icon" onClick={() => delClass(i)} data-testid={`shipping-class-del-${i}`}
                      className="text-red-500 hover:bg-red-50"><Trash2 size={16} /></Button>
            </div>
          ))}
          <Button variant="outline" onClick={addClass} data-testid="shipping-class-add" className="rounded-full">
            <Plus size={15} className="mr-1" /> Klasse hinzufügen
          </Button>
        </div>
        <Button onClick={save} disabled={busy} data-testid="shipping-save"
                className="rounded-full bg-[var(--pop-pink)] text-white font-bold">
          {busy ? "Speichern…" : "Versandklassen speichern"}
        </Button>
      </div>
    </Section>
  );
}

function Gs1Card() {
  const [data, setData] = useState({ entries: [], count: 0 });
  const [items, setItems] = useState([]);
  const [rowMap, setRowMap] = useState({});
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const load = () => { api.get("/settings/gs1").then((r) => setData(r.data)).catch(() => {}); };
  useEffect(() => {
    load();
    api.get("/items", { params: { page_size: 2000 } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
  }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data: res } = await api.post("/settings/gs1/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.count} GS1-Einträge gespeichert`);
      setRowMap({}); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/settings/gs1/apply", { overwrite, mappings: rowMap });
      toast.success(`Übertragen: ${res.updated} aktualisiert · ${res.weights_updated ?? 0} mit Gewicht · ${res.skipped} übersprungen · ${res.unmatched} ohne Zuordnung`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Übertragen fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const matched = data.entries.filter((e) => (rowMap[e.gtin] ?? e.mapped_item_id ?? e.auto_item_id)).length;
  return (
    <Section icon={Package} title="GS1 / GTIN-Produktdaten"
             subtitle="GS1-Export (CSV) hinterlegen. GTINs werden als Barcode und vorhandene Brutto-/Nettogewichte als Versandgewicht (weight_g) auf die zugeordneten Artikel übertragen.">
      <div className="space-y-4" data-testid="gs1-card">
        <div className="flex flex-wrap items-center gap-3">
          <label className="rounded-full border border-[var(--border)] px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-[var(--surface-alt)] inline-flex items-center gap-2" data-testid="gs1-upload-label">
            <Upload size={15} /> {uploading ? "Lädt…" : "GS1-CSV hochladen"}
            <input type="file" accept=".csv" className="hidden" onChange={upload} data-testid="gs1-upload-input" />
          </label>
          {data.count > 0 && <span className="text-sm text-[var(--text-muted)]" data-testid="gs1-count">{data.count} Einträge{data.filename ? ` · ${data.filename}` : ""}</span>}
        </div>
        {data.entries.length > 0 && (
          <>
            <div className="max-h-[360px] overflow-y-auto rounded-xl border border-[var(--border)]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[var(--surface-alt)] text-[11px] text-[var(--text-muted)]">
                  <tr><th className="text-left p-2">GTIN</th><th className="text-left p-2">Produkt</th><th className="text-left p-2">Gewicht</th><th className="text-left p-2">Artikel-Zuordnung</th><th className="p-2 w-8"></th></tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => {
                    const sel = rowMap[e.gtin] ?? e.mapped_item_id ?? e.auto_item_id ?? "";
                    return (
                      <tr key={e.gtin} className="border-t border-[var(--border)]" data-testid={`gs1-row-${e.gtin}`}>
                        <td className="p-2 font-mono text-xs whitespace-nowrap align-top">{e.gtin}</td>
                        <td className="p-2 align-top">{e.name}<span className="text-[var(--text-muted)]"> · {e.net_content}{e.unit ? ` ${e.unit}` : ""}</span></td>
                        <td className="p-2 align-top whitespace-nowrap text-xs">{e.weight_g ? `${e.weight_g} g` : "—"}</td>
                        <td className="p-2 align-top">
                          <select value={sel} onChange={(ev) => setRowMap((m) => ({ ...m, [e.gtin]: ev.target.value }))}
                                  data-testid={`gs1-map-${e.gtin}`}
                                  className="w-full max-w-[240px] rounded-lg border border-[var(--border)] py-1.5 px-2 text-xs bg-white">
                            <option value="">— nicht zuordnen —</option>
                            {items.map((it) => <option key={it.id} value={it.id}>{it.name}{it.sku ? ` (${it.sku})` : ""}</option>)}
                          </select>
                        </td>
                        <td className="p-2 text-center align-top">{e.applied && <CheckCircle2 size={16} className="text-green-600 inline" />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={overwrite} onCheckedChange={setOverwrite} data-testid="gs1-overwrite" />
                Vorhandene Barcodes überschreiben
              </label>
              <Button onClick={apply} disabled={busy || matched === 0} data-testid="gs1-apply"
                      className="rounded-full bg-[var(--pop-pink)] text-white font-bold">
                {busy ? "Überträgt…" : `Auf Artikel übertragen (${matched})`}
              </Button>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

export default function Settings() {
  const [company, setCompany] = useState(null);
  const [integ, setInteg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bundleCfg, setBundleCfg] = useState({});
  const [items, setItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [bundleSaving, setBundleSaving] = useState(false);
  const [logoVersion, setLogoVersion] = useState(Date.now());
  const [uploading, setUploading] = useState(false);
  const [dhl, setDhl] = useState(null);
  const [dhlForm, setDhlForm] = useState({
    sandbox: true, api_key: "", username: "", password: "",
    billing_number: "", profile: "STANDARD_GRUPPENPROFIL",
    sender_name: "", sender_street: "", sender_zip: "", sender_city: "",
  });
  const [dhlSaving, setDhlSaving] = useState(false);
  const [dhlTesting, setDhlTesting] = useState(false);
  const [dhlTestResult, setDhlTestResult] = useState(null);
  const [dsv, setDsv] = useState(null);
  const [dsvForm, setDsvForm] = useState({ api_key: "", account_number: "", api_base: "", sandbox: true });
  const [dsvSaving, setDsvSaving] = useState(false);
  const [confTmpl, setConfTmpl] = useState({ intro_text: "", footer_text: "" });
  const [confSaving, setConfSaving] = useState(false);
  const navigate = useNavigate();

  const loadDsv = async () => {
    try {
      const { data } = await api.get("/integrations/dsv/settings");
      setDsv(data);
      setDsvForm({ api_key: "", account_number: data.account_number || "",
                    api_base: data.api_base || "", sandbox: data.sandbox ?? true });
    } catch (_e) {}
  };

  const saveDsv = async () => {
    setDsvSaving(true);
    try {
      const payload = { ...dsvForm };
      if (!payload.api_key) delete payload.api_key;  // keep existing
      await api.post("/integrations/dsv/settings", payload);
      toast.success(`DSV gespeichert · ${payload.sandbox ? "Sandbox" : "LIVE"}-Modus`);
      setDsvForm((f) => ({ ...f, api_key: "" }));
      loadDsv();
    } catch (err) { toast.error(err.response?.data?.detail || "DSV-Speichern fehlgeschlagen"); }
    finally { setDsvSaving(false); }
  };

  const loadDhl = async () => {
    try {
      const { data } = await api.get("/integrations/dhl/status");
      setDhl(data);
      setDhlForm({
        sandbox: data.sandbox ?? true,
        api_key: "",   // never prefill secrets; empty = keep existing
        username: data.username || "",
        password: "",
        billing_number: data.billing_number || "",
        profile: data.profile || "STANDARD_GRUPPENPROFIL",
        sender_name: data.sender_name || "",
        sender_street: data.sender_street || "",
        sender_zip: data.sender_zip || "",
        sender_city: data.sender_city || "",
      });
    } catch (_e) { /* ignore */ }
  };

  const [packMap, setPackMap] = useState({ per_group_40er: {}, carton_bags: 24 });
  const [packGroups, setPackGroups] = useState([]);
  const [packSaving, setPackSaving] = useState(false);

  const load = () => {
    api.get("/settings/company").then((r) => setCompany(r.data));
    api.get("/settings/integrations").then((r) => setInteg(r.data));
    api.get("/settings/bundles").then((r) => setBundleCfg(r.data)).catch(() => {});
    api.get("/items?type=packaging&page_size=200").then((r) => setItems(r.data)).catch(() => {});
    api.get("/product-groups").then((r) => setPackGroups(r.data)).catch(() => {});
    api.get("/settings/packaging-map").then((r) => setPackMap(r.data || {})).catch(() => {});
    api.get("/recipes").then((r) => setRecipes(r.data)).catch(() => {});
    api.get("/orders/confirmation-template").then((r) => setConfTmpl(r.data || { intro_text: "", footer_text: "" })).catch(() => {});
    loadDhl();
    loadDsv();
    setLogoVersion(Date.now());
  };
  useEffect(() => { load(); }, []);

  const saveDhl = async () => {
    setDhlSaving(true);
    try {
      const { data } = await api.put("/integrations/dhl/settings", dhlForm);
      setDhl(data);
      toast.success(`DHL-Einstellungen gespeichert · ${data.sandbox ? "Sandbox" : "LIVE"}-Modus`);
      // clear secret inputs so they aren't accidentally sent again
      setDhlForm((f) => ({ ...f, api_key: "", password: "" }));
      setDhlTestResult(null);
      // refresh top-level integrations status
      api.get("/settings/integrations").then((r) => setInteg(r.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "DHL-Einstellungen konnten nicht gespeichert werden");
    } finally {
      setDhlSaving(false);
    }
  };

  const testDhl = async () => {
    setDhlTesting(true);
    setDhlTestResult(null);
    try {
      const { data } = await api.post("/integrations/dhl/test");
      setDhlTestResult(data);
      if (data.ok) toast.success(`DHL-Verbindung OK · ${data.sandbox ? "Sandbox" : "LIVE"}`);
      else toast.error(`DHL-Test fehlgeschlagen (${data.status_code || "Fehler"})`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setDhlTestResult({ ok: false, message: msg });
      toast.error("DHL-Test fehlgeschlagen: " + msg);
    } finally {
      setDhlTesting(false);
    }
  };

  const saveCompany = async () => {
    setSaving(true);
    try {
      await api.post("/settings/company", company);
      toast.success("Firmen-Daten gespeichert");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const saveConfTmpl = async () => {
    setConfSaving(true);
    try {
      await api.post("/orders/confirmation-template", confTmpl);
      toast.success("Auftragsbestätigungs-Vorlage gespeichert");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setConfSaving(false); }
  };

  const saveBundles = async () => {
    setBundleSaving(true);
    try {
      await api.post("/settings/bundles", { types: bundleCfg });
      toast.success("Bundle-Konfiguration gespeichert");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setBundleSaving(false); }
  };

  const setPk = (patch) => setPackMap((m) => ({ ...m, ...patch }));
  const setGroup40 = (group, itemId) => setPackMap((m) => ({ ...m, per_group_40er: { ...(m.per_group_40er || {}), [group]: itemId } }));
  const savePackMap = async () => {
    setPackSaving(true);
    try { await api.post("/settings/packaging-map", packMap); toast.success("Verpackungs-Zuordnung gespeichert"); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setPackSaving(false); }
  };
  const updBundle = (code, key, value) => {
    setBundleCfg({ ...bundleCfg, [code]: { ...(bundleCfg[code] || {}), [key]: value } });
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post("/settings/company/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo hochgeladen");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler beim Upload"); }
    finally { setUploading(false); }
  };

  const deleteLogo = async () => {
    if (!window.confirm("Firmen-Logo wirklich entfernen? (Standard-Logo wird wieder verwendet)")) return;
    try {
      await api.delete("/settings/company/logo");
      toast.success("Logo entfernt");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  if (!company || !integ) return <div className="p-8 text-[var(--text-muted)]">Lade…</div>;

  const upd = (k, v) => setCompany({ ...company, [k]: v });

  return (
    <div data-testid="page-settings">
      <PageHeader
        eyebrow="Konfiguration"
        title="Einstellungen"
        description="Firmen-Stammdaten, Integrationen, Rollen & Rechte, Lager-Defaults – alles an einem Ort."
      />

      {/* Firma */}
      <Section icon={Building2} title="Firma & Absender"
               subtitle="Erscheint auf Rechnungen, Lieferscheinen, Angeboten und Versandlabels">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Firmenname (juristische Einheit)</Label>
            <Input data-testid="input-company-name" value={company.name} onChange={(e) => upd("name", e.target.value)} /></div>
          <div><Label>Markenname (Brand)</Label>
            <Input data-testid="input-company-brand" placeholder="z.B. POPCRN"
                   value={company.brand || ""} onChange={(e) => upd("brand", e.target.value)} /></div>
          <div><Label>Geschäftsführer</Label>
            <Input data-testid="input-company-ceo" value={company.ceo || ""} onChange={(e) => upd("ceo", e.target.value)} /></div>
          <div><Label>Straße</Label>
            <Input data-testid="input-company-street" value={company.street} onChange={(e) => upd("street", e.target.value)} /></div>
          <div><Label>Land</Label>
            <Input data-testid="input-company-country" value={company.country} onChange={(e) => upd("country", e.target.value)} /></div>
          <div><Label>PLZ</Label>
            <Input data-testid="input-company-zip" value={company.zip} onChange={(e) => upd("zip", e.target.value)} /></div>
          <div><Label>Ort</Label>
            <Input data-testid="input-company-city" value={company.city} onChange={(e) => upd("city", e.target.value)} /></div>
          <div><Label>USt-IdNr.</Label>
            <Input data-testid="input-company-vatid" value={company.vat_id} onChange={(e) => upd("vat_id", e.target.value)} /></div>
          <div><Label>Steuer-Nr.</Label>
            <Input value={company.tax_id} onChange={(e) => upd("tax_id", e.target.value)} /></div>
          <div><Label>Amtsgericht</Label>
            <Input data-testid="input-company-court" value={company.court || ""} onChange={(e) => upd("court", e.target.value)} /></div>
          <div><Label>HRB-Nr.</Label>
            <Input data-testid="input-company-hrb" value={company.hrb || ""} onChange={(e) => upd("hrb", e.target.value)} /></div>
          <div><Label>Email</Label>
            <Input type="email" value={company.email} onChange={(e) => upd("email", e.target.value)} /></div>
          <div><Label>Telefon</Label>
            <Input value={company.phone} onChange={(e) => upd("phone", e.target.value)} /></div>
          <div><Label>Reply-To (für ausgehende Mails)</Label>
            <Input type="email" value={company.reply_to} onChange={(e) => upd("reply_to", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Reklamations-E-Mail (intern)
              <span className="text-[10px] text-[var(--text-muted)] ml-2">
                (Empfänger für neue Portal-Reklamationen · leer = Admin-Adresse)
              </span>
            </Label>
            <Input type="email" data-testid="input-company-complaints-email"
                   placeholder="z.B. reklamation@popcrn.de"
                   value={company.complaints_email || ""}
                   onChange={(e) => upd("complaints_email", e.target.value)} /></div>
          <div><Label>Bank</Label>
            <Input value={company.bank_name} onChange={(e) => upd("bank_name", e.target.value)} /></div>
          <div><Label>IBAN</Label>
            <Input value={company.iban} onChange={(e) => upd("iban", e.target.value)} className="font-mono" /></div>
          <div><Label>BIC</Label>
            <Input value={company.bic} onChange={(e) => upd("bic", e.target.value)} className="font-mono" /></div>
          <div className="md:col-span-2">
            <Label>PayPal.me Handle
              <span className="text-[10px] text-[var(--text-muted)] ml-2">
                (optional · nur Handle ohne https://)
              </span>
            </Label>
            <Input data-testid="input-company-paypal"
                   placeholder="z.B. popcrn  →  https://paypal.me/popcrn"
                   value={company.paypal_me_handle || ""}
                   onChange={(e) => upd("paypal_me_handle", e.target.value)}
                   className="font-mono" />
            <div className="text-xs text-[var(--text-muted)] mt-1">
              Wird als klickbarer PayPal-QR auf Rechnung und Mahnung gedruckt und ist in der Rechnungsübersicht per „Zahlungslink“ teilbar.
            </div>
          </div>
          <div><Label>Website</Label>
            <Input value={company.website || ""} onChange={(e) => upd("website", e.target.value)} /></div>
          <div><Label>AGB-Link (URL)</Label>
            <Input data-testid="input-company-agb"
                   placeholder="https://popcrn.de/agb"
                   value={company.agb_url || ""} onChange={(e) => upd("agb_url", e.target.value)} /></div>
          <div><Label>Datenschutz-Link (URL)</Label>
            <Input data-testid="input-company-privacy"
                   placeholder="https://popcrn.de/datenschutz"
                   value={company.privacy_url || ""} onChange={(e) => upd("privacy_url", e.target.value)} /></div>
          <div><Label>Zahlungsziel (Tage)</Label>
            <Input type="number" min="0" max="180"
                   value={company.default_payment_terms_days}
                   onChange={(e) => upd("default_payment_terms_days", Number(e.target.value))} /></div>
          <div><Label>MHD-Warnung (Tage)</Label>
            <Input type="number" min="1" max="365" data-testid="input-company-mhd-warning"
                   value={company.mhd_warning_days ?? 30}
                   onChange={(e) => upd("mhd_warning_days", Number(e.target.value))} />
            <div className="text-xs text-[var(--text-muted)] mt-1">Chargen deren MHD binnen dieser Frist erreicht wird, erscheinen im Dashboard als „bald ablaufend&quot;.</div></div>
          <div><Label>Paletten-Schwelle für LKW-Transport</Label>
            <Input type="number" min="1" max="10000" data-testid="input-pallet-threshold"
                   value={company.pallet_transport_threshold ?? 600}
                   onChange={(e) => upd("pallet_transport_threshold", Number(e.target.value))} />
            <div className="text-xs text-[var(--text-muted)] mt-1">Ab dieser Anzahl offener Paletten pro Kunde wird automatisch ein LKW-Transport vorgeschlagen.</div></div>
          <div className="flex items-end gap-3 pb-1">
            <div className="flex-1">
              <Label>Palettentausch beim Warenausgang bevorzugen</Label>
              <div className="text-xs text-[var(--text-muted)] mt-1">Wenn aktiv: Beim Lieferschein wird „Paletten zurück" automatisch auf „Paletten hin" gesetzt.</div>
            </div>
            <Switch
              checked={company.pallet_exchange_default !== false}
              onCheckedChange={(v) => upd("pallet_exchange_default", v)}
              data-testid="switch-pallet-exchange-default"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveCompany} disabled={saving} data-testid="btn-save-company"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </Section>

      {/* Auftragsbestätigung-Vorlage */}
      <Section icon={Mail} title="Auftragsbestätigung (E-Mail & PDF)"
               subtitle="Begrüßung & Fußzeile für die Auftragsbestätigung. Logo & Absender stammen aus „Firma & Absender“.">
        <div className="space-y-4" data-testid="order-confirmation-template">
          <div>
            <Label>Begrüßungstext</Label>
            <textarea data-testid="input-conf-intro" rows={3}
                      className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-sm"
                      placeholder="z.B. vielen Dank für Ihre Bestellung! Anbei bestätigen wir Ihren Auftrag."
                      value={confTmpl.intro_text || ""}
                      onChange={(e) => setConfTmpl({ ...confTmpl, intro_text: e.target.value })} />
          </div>
          <div>
            <Label>Fußzeile</Label>
            <textarea data-testid="input-conf-footer" rows={3}
                      className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-sm"
                      placeholder="z.B. Bei Fragen erreichen Sie uns unter …"
                      value={confTmpl.footer_text || ""}
                      onChange={(e) => setConfTmpl({ ...confTmpl, footer_text: e.target.value })} />
          </div>
          <div className="flex justify-end">
            <Button onClick={saveConfTmpl} disabled={confSaving} data-testid="btn-save-conf-template"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              {confSaving ? "Speichere…" : "Speichern"}
            </Button>
          </div>
        </div>
      </Section>

      {/* Bundle-Konfiguration */}
      <ProductGroupsSection />
      <WarehouseFefoCard />
      <Display120DefaultCard />
      <ShippingClassesCard />
      <Gs1Card />

      {/* Runde 126.9 – Freie Bundle-Größen (Karton / Sack / Display / Sonstiges) */}
      <Section icon={Package} title="Bundle-Größen"
               subtitle="Karton · Sack · Display · Sonstiges. Beim Produktionsabschluss werden Verpackungsmaterialien anhand dieser Zuordnung abgebucht.">
        <div className="space-y-3" data-testid="bundle-config">
          {Object.entries(bundleCfg).map(([code, cfg]) => {
            const isDisplay = (cfg.type || "").toLowerCase() === "display";
            const variants = cfg.variants || [];
            const variantSum = variants.reduce((s, v) => s + (Number(v.count) || 0), 0);
            const pieces = variants.length || 1;
            const perVariant = pieces > 0 ? Math.floor((cfg.units || 0) / pieces) : 0;
            return (
              <div key={code} className="rounded-xl border border-[var(--border)] p-3 space-y-2 bg-white/60" data-testid={`bundle-row-${code}`}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div><Label className="text-[10px]">Name / Code</Label>
                    <Input value={cfg.label || code} data-testid={`bundle-name-${code}`}
                           onChange={(e) => updBundle(code, "label", e.target.value)} /></div>
                  <div><Label className="text-[10px]">Typ</Label>
                    <Select value={cfg.type || "Sonstiges"}
                            onValueChange={(v) => updBundle(code, "type", v)}>
                      <SelectTrigger data-testid={`bundle-type-${code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Karton">Karton</SelectItem>
                        <SelectItem value="Sack">Sack</SelectItem>
                        <SelectItem value="Display">Display</SelectItem>
                        <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-[10px]">Inhaltsmenge (Stk)</Label>
                    <Input type="number" min="1" value={cfg.units ?? ""}
                           data-testid={`bundle-units-${code}`}
                           onChange={(e) => updBundle(code, "units", Number(e.target.value))}
                           className="text-right font-mono" /></div>
                  <div><Label className="text-[10px]">Gewicht gesamt (g)</Label>
                    <Input type="number" step="any" value={cfg.weight_g ?? ""}
                           data-testid={`bundle-weight-${code}`}
                           placeholder="optional"
                           onChange={(e) => updBundle(code, "weight_g", e.target.value === "" ? null : Number(e.target.value))}
                           className="text-right font-mono" /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><Label className="text-[10px]">Verpackungs-Artikel (Karton/Sack)</Label>
                    <Select value={cfg.packaging_item_id || "none"}
                            onValueChange={(v) => updBundle(code, "packaging_item_id", v === "none" ? null : v)}>
                      <SelectTrigger data-testid={`bundle-pkg-${code}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Kein Verpackungsmaterial</SelectItem>
                        {items.filter((i) => i.type === "packaging").map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-[10px]">Beschreibung</Label>
                    <Input value={cfg.description || ""}
                           data-testid={`bundle-desc-${code}`}
                           placeholder="optional"
                           onChange={(e) => updBundle(code, "description", e.target.value)} /></div>
                </div>
                <div>
                  <Label className="text-[10px]">Rezeptur-Zuordnung (leer = für alle Rezepturen)</Label>
                  <div className="flex gap-2">
                    <Select value="none"
                            onValueChange={(v) => {
                              if (v === "none") return;
                              const rec = recipes.find((r) => r.id === v);
                              const oid = rec?.output_item_id;
                              if (!oid) return;
                              const cur = cfg.product_item_ids || [];
                              if (!cur.includes(oid)) updBundle(code, "product_item_ids", [...cur, oid]);
                            }}>
                      <SelectTrigger data-testid={`bundle-prod-add-${code}`}><SelectValue placeholder={cfg.product_item_ids?.length ? `${cfg.product_item_ids.length} Rezeptur(en) zugeordnet` : "Für alle Rezepturen · optional zuweisen"} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Rezeptur hinzufügen —</SelectItem>
                        {recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.output_item_sku ? ` · ${r.output_item_sku}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button type="button"
                            data-testid={`bundle-prod-all-${code}`}
                            onClick={() => {
                              const all = [...new Set(recipes.map((r) => r.output_item_id).filter(Boolean))];
                              updBundle(code, "product_item_ids", all);
                              toast.success(`${all.length} Rezeptur-Produkte zugewiesen`);
                            }}
                            className="whitespace-nowrap text-[11px] font-semibold rounded-full border border-[var(--border)] px-3 hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]"
                            title="Alle Rezepturen zuweisen — praktisch für 13er/Universal-Kartons">
                      Alle zuweisen
                    </button>
                    {(cfg.product_item_ids || []).length > 0 && (
                      <button type="button"
                              data-testid={`bundle-prod-clear-${code}`}
                              onClick={() => updBundle(code, "product_item_ids", [])}
                              className="whitespace-nowrap text-[11px] font-semibold rounded-full border border-[var(--border)] px-3 hover:border-red-500 hover:text-red-600"
                              title="Alle Rezeptur-Zuordnungen entfernen">
                        Zurücksetzen
                      </button>
                    )}
                  </div>
                  {(cfg.product_item_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {cfg.product_item_ids.map((pid) => {
                        const rec = recipes.find((x) => x.output_item_id === pid);
                        const label = rec ? (rec.output_item_sku || rec.output_item_name || rec.name) : pid;
                        return (
                          <span key={pid} className="pop-pill bg-slate-100 text-slate-700 text-[10px]">
                            {label}
                            <button onClick={() => updBundle(code, "product_item_ids", cfg.product_item_ids.filter((x) => x !== pid))} className="ml-1 text-red-600">×</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {isDisplay && (
                  <div className="rounded-lg bg-pink-50 border border-pink-200 p-2 space-y-2">
                    <div className="text-[11px] font-semibold text-pink-900">Display-Sorten (Beutel je Sorte = {perVariant} · Summe {variantSum} · Total {cfg.units})</div>
                    {(variants).map((v, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_100px_28px] gap-2">
                        <Select value={v.item_id || "none"}
                                onValueChange={(val) => {
                                  const next = [...variants]; next[idx] = { ...next[idx], item_id: val === "none" ? "" : val };
                                  updBundle(code, "variants", next);
                                }}>
                          <SelectTrigger data-testid={`bundle-variant-${code}-${idx}`}><SelectValue placeholder="Sorte wählen" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {items.filter((i) => i.type === "finished" || i.type === "finished_good").map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="number" min="1" placeholder={`${perVariant}`}
                               value={v.count ?? ""}
                               data-testid={`bundle-variant-count-${code}-${idx}`}
                               onChange={(e) => {
                                 const next = [...variants]; next[idx] = { ...next[idx], count: Number(e.target.value) };
                                 updBundle(code, "variants", next);
                               }}
                               className="text-right font-mono" />
                        <button onClick={() => updBundle(code, "variants", variants.filter((_, i) => i !== idx))} className="text-red-600 text-lg">×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => updBundle(code, "variants", [...variants, { item_id: "", count: perVariant }])}
                      className="text-xs font-semibold text-[var(--pop-pink)] hover:underline">
                      + Sorte hinzufügen
                    </button>
                  </div>
                )}
                <div className="flex justify-end">
                  <button type="button"
                          data-testid={`bundle-delete-${code}`}
                          onClick={async () => {
                            if (!window.confirm(`Bundle „${cfg.label || code}" löschen?`)) return;
                            try {
                              await api.delete(`/settings/bundles/${encodeURIComponent(code)}`);
                              const next = { ...bundleCfg }; delete next[code]; setBundleCfg(next);
                              toast.success("Bundle gelöscht");
                            } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
                          }}
                          className="text-xs text-red-600 hover:underline">
                    Löschen
                  </button>
                </div>
              </div>
            );
          })}
          <button type="button"
                  data-testid="btn-add-bundle"
                  onClick={() => {
                    const code = window.prompt("Interner Code für neues Bundle (z.B. 'sack-5kg')");
                    if (!code) return;
                    const trimmed = code.trim();
                    if (!trimmed || bundleCfg[trimmed]) { toast.error("Code leer oder bereits vergeben"); return; }
                    setBundleCfg({ ...bundleCfg, [trimmed]: {
                      label: trimmed, units: 1, type: "Sonstiges",
                      weight_g: null, description: "", packaging_item_id: null,
                      product_item_ids: [], variants: [],
                    }});
                  }}
                  className="w-full rounded-xl border-2 border-dashed border-[var(--border)] py-3 text-sm font-semibold text-[var(--text-muted)] hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)]">
            + Bundle-Größe hinzufügen
          </button>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveBundles} disabled={bundleSaving} data-testid="btn-save-bundles"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            {bundleSaving ? "Speichere…" : "Bundle-Größen speichern"}
          </Button>
        </div>
      </Section>

      <Section icon={Package} title="Verpackungs-Zuordnung (Auftrag)"
               subtitle="Je Warengruppe ein 40er-Karton; 13er & Umkarton warengruppenunabhängig. Wird bei der Auto-Verpackung in Bestellungen berechnet.">
        <div className="space-y-4" data-testid="packaging-map-config">
          <div>
            <Label className="overline mb-1 block">40er-Karton je Warengruppe (40 Tüten/Karton)</Label>
            <div className="space-y-2">
              {packGroups.length === 0 && <div className="text-xs text-[var(--text-muted)]">Keine Warengruppen angelegt.</div>}
              {packGroups.map((g) => (
                <div key={g.id} className="flex items-center gap-2">
                  <span className="w-40 shrink-0 text-sm truncate">{g.name}</span>
                  <select value={(packMap.per_group_40er || {})[g.name] || ""}
                          onChange={(e) => setGroup40(g.name, e.target.value)}
                          data-testid={`pkgmap-40er-${g.name}`}
                          className="flex-1 h-9 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 text-sm">
                    <option value="">— nicht zugeordnet —</option>
                    {items.map((i) => <option key={i.id} value={i.id}>{i.sku} · {i.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="overline mb-1 block">13er-Display (13 Tüten)</Label>
              <select value={packMap.box13_item_id || ""} onChange={(e) => setPk({ box13_item_id: e.target.value })}
                      data-testid="pkgmap-13er"
                      className="w-full h-9 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 text-sm">
                <option value="">— nicht zugeordnet —</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.sku} · {i.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="overline mb-1 block">Umkarton</Label>
              <select value={packMap.carton_item_id || ""} onChange={(e) => setPk({ carton_item_id: e.target.value })}
                      data-testid="pkgmap-carton"
                      className="w-full h-9 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 text-sm">
                <option value="">— nicht zugeordnet —</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.sku} · {i.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="overline mb-1 block">Tüten je Umkarton</Label>
              <Input type="number" min="1" value={packMap.carton_bags ?? 24}
                     data-testid="pkgmap-carton-bags"
                     onChange={(e) => setPk({ carton_bags: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={savePackMap} disabled={packSaving} data-testid="btn-save-packaging-map"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              {packSaving ? "Speichere…" : "Zuordnung speichern"}
            </Button>
          </div>
        </div>
      </Section>

      {/* Logo */}
      <Section icon={Upload} title="Firmen-Logo"
               subtitle="Erscheint oben auf allen PDFs (Rechnungen, Lieferscheine, Angebote, Etiketten)">
        <div className="flex items-center gap-6 flex-wrap">
          {(company.has_logo || company.logo_path || company.logo_media_type) ? (
            <div className="w-40 h-24 bg-neutral-900 rounded-xl p-3 flex items-center justify-center">
              <img
                key={logoVersion}
                src={`${API_BASE}/settings/company/logo?v=${logoVersion}`}
                alt="Logo"
                data-testid="img-company-logo"
                className="max-h-16 max-w-full object-contain"
              />
            </div>
          ) : (
            <div className="w-40 h-24 border-2 border-dashed border-[var(--border)] rounded-xl flex items-center justify-center text-xs text-[var(--text-muted)] text-center px-3">
              Kein eigenes Logo · Standard-Logo aktiv
            </div>
          )}
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold px-4 py-2 text-sm cursor-pointer">
              <Upload size={14} /> {uploading ? "Lade hoch…" : "Logo hochladen"}
              <input type="file" accept="image/png,image/jpeg" onChange={uploadLogo}
                     data-testid="input-logo-upload" className="hidden" />
            </label>
            {(company.has_logo || company.logo_path || company.logo_media_type) && (
              <button onClick={deleteLogo} data-testid="btn-delete-logo"
                      className="inline-flex items-center gap-1 text-xs text-[var(--danger)] hover:underline">
                <Trash2 size={12} /> Logo entfernen
              </button>
            )}
            <div className="text-xs text-[var(--text-muted)]">
              PNG oder JPG · max. 3 MB · idealerweise transparenter Hintergrund
            </div>
          </div>
        </div>
      </Section>

      {/* Integrationen Overview */}
      <Section icon={Cloud} title="Integrationen" subtitle="Externe Dienste – Details in der Integrationen-Seite"
               right={<Button variant="outline" onClick={() => navigate("/integrationen")}
                              data-testid="btn-open-integrations" className="rounded-full">
                       Öffnen <ChevronRight size={14} className="ml-1" />
                     </Button>}>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="border border-[var(--border)] rounded-xl p-3 flex items-center justify-between gap-3">
            <div className="text-sm">
              <div className="font-semibold">WooCommerce</div>
              <div className="text-xs text-[var(--text-muted)]">{integ.woocommerce.url || "keine URL"}</div>
            </div>
            <StatusPill ok={integ.woocommerce.configured} />
          </div>
          <div className="border border-[var(--border)] rounded-xl p-3 flex items-center justify-between gap-3">
            <div className="text-sm">
              <div className="font-semibold">Resend E-Mail</div>
              <div className="text-xs text-[var(--text-muted)]">{integ.email_resend.from_name || "kein Absender"}</div>
            </div>
            <StatusPill ok={integ.email_resend.configured} />
          </div>
        </div>
      </Section>

      {/* Versand-Carrier */}
      <Section icon={Truck} title="Versand-Carrier"
               subtitle="Etiketten, Tracking-Nummern, Sendungsbuchung">
        {/* --- DHL: editable settings + Sandbox/Live toggle + Test --- */}
        {dhl && (
          <div className="border-2 rounded-2xl p-4 mb-4 space-y-4"
               data-testid="dhl-settings-card"
               style={{ borderColor: dhl.sandbox ? "var(--warning)" : "var(--success)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-lg">DHL Parcel DE (v2)</div>
                  <StatusPill ok={dhl.configured} label={dhl.configured ? "Bereit" : "Nicht konfiguriert"} />
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${dhl.sandbox ? "bg-[var(--warning-bg)] text-[var(--warning)]" : "bg-[var(--success-bg)] text-[var(--success)]"}`}>
                    {dhl.sandbox ? "SANDBOX" : "LIVE"}
                  </span>
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  {dhl.configured ? `Billing ${dhl.billing_number || "—"} · User ${dhl.username || "—"}` : "API-Key, User, Passwort und Abrechnungsnummer eintragen"}
                </div>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer" title="Sandbox = Testumgebung, Live = echte Sendungen">
                <span className="text-xs font-semibold">Sandbox</span>
                <Switch
                  checked={!dhlForm.sandbox}
                  onCheckedChange={(v) => setDhlForm({ ...dhlForm, sandbox: !v })}
                  data-testid="switch-dhl-live"
                />
                <span className="text-xs font-semibold">Live</span>
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>API-Key {dhl.api_key_set && <span className="text-[10px] text-[var(--text-muted)]">(gesetzt · leer lassen um zu behalten)</span>}</Label>
                <Input type="password" placeholder={dhl.api_key_set ? "••••••••" : "aus DHL Developer Portal"}
                       value={dhlForm.api_key} data-testid="input-dhl-api-key"
                       onChange={(e) => setDhlForm({ ...dhlForm, api_key: e.target.value })} />
              </div>
              <div>
                <Label>Abrechnungsnummer (14-stellig)</Label>
                <Input value={dhlForm.billing_number} data-testid="input-dhl-billing"
                       maxLength={14} placeholder="z.B. 22222222220101"
                       onChange={(e) => setDhlForm({ ...dhlForm, billing_number: e.target.value })} />
              </div>
              <div>
                <Label>GKP Benutzername</Label>
                <Input value={dhlForm.username} data-testid="input-dhl-username"
                       onChange={(e) => setDhlForm({ ...dhlForm, username: e.target.value })} />
              </div>
              <div>
                <Label>GKP Passwort {dhl.password_set && <span className="text-[10px] text-[var(--text-muted)]">(gesetzt · leer lassen um zu behalten)</span>}</Label>
                <Input type="password" placeholder={dhl.password_set ? "••••••••" : "GKP Passwort"}
                       value={dhlForm.password} data-testid="input-dhl-password"
                       onChange={(e) => setDhlForm({ ...dhlForm, password: e.target.value })} />
              </div>
            </div>

            <div>
              <Label className="overline">Absender-Adresse (auf Etikett)</Label>
              <div className="grid md:grid-cols-4 gap-3 mt-1">
                <Input placeholder="Firmenname" value={dhlForm.sender_name} data-testid="input-dhl-sender-name"
                       onChange={(e) => setDhlForm({ ...dhlForm, sender_name: e.target.value })} />
                <Input placeholder="Straße + Nr" value={dhlForm.sender_street} data-testid="input-dhl-sender-street"
                       onChange={(e) => setDhlForm({ ...dhlForm, sender_street: e.target.value })} />
                <Input placeholder="PLZ" value={dhlForm.sender_zip} data-testid="input-dhl-sender-zip"
                       onChange={(e) => setDhlForm({ ...dhlForm, sender_zip: e.target.value })} />
                <Input placeholder="Ort" value={dhlForm.sender_city} data-testid="input-dhl-sender-city"
                       onChange={(e) => setDhlForm({ ...dhlForm, sender_city: e.target.value })} />
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={saveDhl} disabled={dhlSaving}
                      data-testid="btn-save-dhl"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                <Save size={14} className="mr-1" /> {dhlSaving ? "Speichere…" : "Speichern"}
              </Button>
              <Button onClick={testDhl} disabled={dhlTesting || !dhl.configured}
                      data-testid="btn-test-dhl"
                      variant="outline" className="rounded-full">
                <RefreshCw size={14} className={`mr-1 ${dhlTesting ? "animate-spin" : ""}`} />
                {dhlTesting ? "Teste…" : "Verbindung testen"}
              </Button>
              {!dhlForm.sandbox && (
                <span className="text-xs font-semibold text-[var(--danger)]">
                  ⚠ Live-Modus: Buchungen erzeugen echte Sendungen und werden berechnet.
                </span>
              )}
            </div>

            {dhlTestResult && (
              <div data-testid="dhl-test-result"
                   className={`text-xs rounded-xl p-3 ${dhlTestResult.ok ? "bg-[var(--success-bg)] text-[var(--success)]" : "bg-[var(--danger-bg)] text-[var(--danger)]"}`}>
                {dhlTestResult.ok
                  ? `✓ Verbindung OK (${dhlTestResult.sandbox ? "Sandbox" : "LIVE"})`
                  : `✗ ${dhlTestResult.status_code ? `[${dhlTestResult.status_code}] ` : ""}${dhlTestResult.message}`}
              </div>
            )}
          </div>
        )}

        {/* DSV-Integration */}
        {dsv && (
          <div className="rounded-2xl border-2 border-[#003580]/30 bg-[#003580]/5 p-4 space-y-3" data-testid="dsv-settings-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-black text-lg text-[#003580]">🚚 DSV</div>
                <div className="text-xs text-[var(--text-muted)]">
                  Versandaufträge und Sendungsverfolgung · {dsv.configured ? "Konfiguriert" : "Noch nicht konfiguriert"}
                </div>
              </div>
              <span className={`pop-pill text-[10px] font-bold ${dsvForm.sandbox ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>
                {dsvForm.sandbox ? "SANDBOX" : "LIVE"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">API-Key</Label>
                <Input type="password" placeholder={dsv.configured ? "(unverändert lassen)" : "z. B. dsv_live_…"}
                        data-testid="input-dsv-api-key"
                        value={dsvForm.api_key}
                        onChange={(e) => setDsvForm({ ...dsvForm, api_key: e.target.value })} /></div>
              <div><Label className="text-xs">Kundenkontonummer</Label>
                <Input data-testid="input-dsv-account"
                        value={dsvForm.account_number}
                        onChange={(e) => setDsvForm({ ...dsvForm, account_number: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">API-Basis-URL</Label>
                <Input placeholder="https://api.dsv.com/v1" data-testid="input-dsv-api-base"
                        value={dsvForm.api_base}
                        onChange={(e) => setDsvForm({ ...dsvForm, api_base: e.target.value })} /></div>
              <label className="inline-flex items-center gap-2 text-xs col-span-2">
                <input type="checkbox" checked={dsvForm.sandbox}
                        data-testid="chk-dsv-sandbox"
                        onChange={(e) => setDsvForm({ ...dsvForm, sandbox: e.target.checked })} />
                <b>Sandbox-Modus</b> (Mock-Labels + Mock-Tracking, keine echten Buchungen)
              </label>
            </div>
            <Button onClick={saveDsv} disabled={dsvSaving}
                     data-testid="btn-save-dsv"
                     className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              <Save size={14} className="mr-1" /> {dsvSaving ? "Speichere…" : "Speichern"}
            </Button>
            {!dsvForm.sandbox && (
              <div className="text-xs font-semibold text-[var(--danger)]">
                ⚠ Live-Modus: Buchungen erzeugen echte DSV-Sendungen und werden abgerechnet.
              </div>
            )}
          </div>
        )}

        {/* Andere Carrier (Phase-2) */}
        <div className="space-y-2">
          {[
            { key: "dpd", label: "DPD", ok: false, meta: integ.dpd.status },
            { key: "dsv", label: "DSV", ok: false, meta: integ.dsv.status },
          ].map((c) => (
            <div key={c.key} data-testid={`carrier-row-${c.key}`}
                 className="border border-[var(--border)] rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="font-semibold">{c.label}</div>
                <div className="text-xs text-[var(--text-muted)]">{c.meta}</div>
              </div>
              <StatusPill ok={c.ok} label="Phase-2" />
            </div>
          ))}
        </div>
      </Section>

      {/* Rollen & Rechte */}
      <Section icon={Users} title="Benutzerrechte"
               subtitle={`${integ.roles.total_users} Nutzer · Rollen: Admin, Production, Warehouse, Sales`}
               right={<Button variant="outline" onClick={() => navigate("/benutzer")}
                              data-testid="btn-open-users" className="rounded-full">
                       Benutzer verwalten <ChevronRight size={14} className="ml-1" />
                     </Button>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["admin", "production", "warehouse", "sales"].map((r) => (
            <div key={r} className="border border-[var(--border)] rounded-xl p-3 text-center">
              <div className="text-xs uppercase text-[var(--text-muted)]">{r}</div>
              <div className="font-display text-2xl font-bold">{integ.roles.counts[r] || 0}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-[var(--text-muted)] space-y-1">
          <div><b>Admin:</b> vollen Zugriff auf alle Module und Einstellungen</div>
          <div><b>Production:</b> Rezepte, Produktionsaufträge, Chargen freigeben</div>
          <div><b>Warehouse:</b> Wareneingang, Chargen umlagern, Bestand pflegen</div>
          <div><b>Sales:</b> Kunden, Bestellungen, Lieferscheine, Rechnungen</div>
        </div>
      </Section>

      {/* Lager */}
      <Section icon={Warehouse} title="Lager & Warehouse"               subtitle="Regale, Standort-Konventionen, Auto-FEFO">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="border border-[var(--border)] rounded-xl p-4">
            <div className="font-semibold mb-1">Regal-Übersicht</div>
            <div className="text-xs text-[var(--text-muted)] mb-2">
              Zeigt alle Chargen nach Regalplatz gruppiert. Regal-Etiketten mit QR-Code drucken.
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/regale")}
                    data-testid="btn-open-shelves" className="rounded-full">
              Zu den Regalen <ExternalLink size={12} className="ml-1" />
            </Button>
          </div>
          <div className="border border-[var(--border)] rounded-xl p-4">
            <div className="font-semibold mb-1">FEFO-Automatik</div>
            <div className="text-xs text-[var(--text-muted)] mb-2">
              First-Expired-First-Out: Aktiv bei Produktions-Verbrauch und Lieferschein-Zuweisung.
              Nur QC-freigegebene Chargen werden ausgewählt.
            </div>
            <StatusPill ok label="Immer aktiv" />
          </div>
          <div className="border border-[var(--border)] rounded-xl p-4">
            <div className="font-semibold mb-1">Bestellvorschläge</div>
            <div className="text-xs text-[var(--text-muted)] mb-2">
              Automatische Nachbestell-Vorschläge basierend auf Min-Bestand und Lieferant-Zuordnung.
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/bestellvorschlaege")}
                    data-testid="btn-open-reorder" className="rounded-full">
              Öffnen <ExternalLink size={12} className="ml-1" />
            </Button>
          </div>
          <div className="border border-[var(--border)] rounded-xl p-4">
            <div className="font-semibold mb-1">Quality Release</div>
            <div className="text-xs text-[var(--text-muted)] mb-2">
              Neue Chargen sind <code>pending</code> bis zur QC-Freigabe. Versand blockt bei ungefreigebenen Chargen.
            </div>
            <StatusPill ok label="Aktiv" />
          </div>
        </div>
      </Section>

      {/* Benachrichtigungen */}
      <Section icon={Mail} title="E-Mail & Benachrichtigungen"
               subtitle="Zahlungserinnerungen, Min-Bestand-Alerts, Rechnungsversand"
               right={<Button variant="outline" onClick={() => navigate("/integrationen")}
                              data-testid="btn-open-notifications" className="rounded-full">
                       Konfigurieren <ChevronRight size={14} className="ml-1" />
                     </Button>}>
        <div className="text-sm space-y-2">
          <div className="flex items-center gap-2">
            <StatusPill ok={integ.email_resend.configured} label={integ.email_resend.configured ? "Bereit" : "EMERGENT_EMAIL_KEY fehlt"} />
            <span className="text-[var(--text-muted)]">Emergent-managed Resend</span>
          </div>
          <div className="text-xs text-[var(--text-muted)]">
            Reply-To: <span className="font-mono">{integ.reply_to_email || "—"}</span>
          </div>
        </div>
        <PalletRemindersBlock />
      </Section>
      {/* Backup & Wiederherstellung */}
      <Section icon={HardDrive} title="Backup & Wiederherstellung"
               subtitle="Manuelle und geplante Datenbank-Backups, optional in Nextcloud sichern">
        <BackupSection />
      </Section>

      {/* Standard-Lagerplan erzeugen */}
      <WarehouseLayoutCard />

      {/* Regale via Excel importieren */}
      <ShelvesImportCard />

      {/* Danger Zone — Testbestellungen löschen */}
      <CleanupOrdersCard />
    </div>
  );
}

function PalletRemindersBlock() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/pallets/reminders/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pallets/reminders/settings", s);
      setS(data);
      toast.success("Palettenkonto-Mahnung gespeichert");
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pallets/reminders/run");
      if (data.skipped) toast.warning(`Übersprungen: ${data.skipped}`);
      else toast.success(`${data.sent || 0} Mails versendet · ${data.skipped || 0} übersprungen`);
      const r = await api.get("/pallets/reminders/settings");
      setS(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div className="mt-4 border-t pt-4" data-testid="pallet-reminders-block">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold text-sm">Palettenkonto-Mahnung</div>
          <div className="text-xs text-[var(--text-muted)]">Automatische E-Mail an Kunden, deren offener Palettensaldo ≥ Schwelle liegt.</div>
        </div>
        <Switch checked={!!s.enabled}
                onCheckedChange={(v) => setS({ ...s, enabled: v })}
                data-testid="switch-pallet-reminders" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <Label>Schwelle (offene Paletten)</Label>
          <Input type="number" min="1" data-testid="input-pallet-reminder-threshold"
                 value={s.threshold}
                 onChange={(e) => setS({ ...s, threshold: Number(e.target.value) })} />
        </div>
        <div>
          <Label>Versandzeit (UTC-Stunde)</Label>
          <Input type="number" min="0" max="23"
                 value={s.hour_utc}
                 onChange={(e) => setS({ ...s, hour_utc: Number(e.target.value) })} />
        </div>
        <div>
          <Label>Mindest-Abstand zwischen Mails (Tage)</Label>
          <Input type="number" min="1" max="365"
                 value={s.min_days_between}
                 onChange={(e) => setS({ ...s, min_days_between: Number(e.target.value) })} />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={save} disabled={busy}
                data-testid="btn-save-pallet-reminders"
                className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
          {busy ? "Speichere…" : "Speichern"}
        </Button>
        <Button size="sm" variant="outline" onClick={runNow} disabled={busy || !s.enabled}
                data-testid="btn-run-pallet-reminders"
                className="rounded-full">
          Jetzt Mahnlauf starten
        </Button>
        {s.last_run && (
          <span className="text-xs text-[var(--text-muted)]">
            Zuletzt: {new Date(s.last_run).toLocaleString("de-DE")}
            {s.last_result?.sent != null && ` · ${s.last_result.sent} versendet`}
          </span>
        )}
      </div>
    </div>
  );
}
